<template>
  <label
    class="flex items-center justify-between gap-3 p-3 rounded-lg border"
    :class="[
      selectable
        ? isNoBankAccount
          ? 'cursor-pointer hover:bg-orange-50 dark:hover:bg-orange-900/20 border-orange-200 dark:border-orange-700/60'
          : 'cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-800 border-surface-200 dark:border-surface-700'
        : 'border-surface-200 dark:border-surface-700',
      isSelected
        ? isNoBankAccount
          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700/60'
          : 'bg-surface-100 dark:bg-surface-800/20 border-surface-300 dark:border-surface-700/60'
        : '',
    ]"
    :for="inputId"
    :data-test-id="dataTestId"
  >
    <span class="flex items-center gap-3 flex-1 min-w-0">
      <div v-if="selectable" class="flex-shrink-0">
        <RadioButton
          v-model="radioValue"
          :value="true"
          :input-id="inputId"
          :data-test-id="`${dataTestId}-radio`"
        />
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <i
            v-if="isNoBankAccount"
            class="pi pi-exclamation-triangle text-orange-500"
          ></i>
          <i
            v-else
            class="pi pi-building-columns text-surface-400 dark:text-surface-500"
          ></i>
          <span class="text-sm font-medium text-surface-900 dark:text-surface-0 truncate">
            {{ displayName }}
          </span>
          <span
            v-if="!isNoBankAccount && bankAccount?.IsReach"
            class="inline-flex items-center gap-1 rounded-full bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-200 dark:border-green-700 px-2 py-0.5 text-xs font-medium"
            data-test-id="reach-badge"
          >
            <i class="pi pi-check text-[10px]"></i>
            Reach
          </span>
        </div>
      </div>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import RadioButton from 'primevue/radiobutton'
import type { ShopifyBankAccount } from '@/types/shopifyPayouts'

interface Props {
  bankAccount?: ShopifyBankAccount
  selectable?: boolean
  selectedId?: string | null
  isNoBankAccount?: boolean
  noBankAccountLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  selectable: false,
  selectedId: null,
  isNoBankAccount: false,
  noBankAccountLabel: 'No Bank Account',
})

const emit = defineEmits<{
  select: [value: string | null]
}>()

const isSelected = computed(() => {
  if (!props.selectable) return false
  if (props.isNoBankAccount) {
    return props.selectedId === null
  }
  return props.selectedId === props.bankAccount?.Id
})

const radioValue = ref(isSelected.value)

watch(
  isSelected,
  newValue => {
    radioValue.value = newValue
  },
  { immediate: true },
)

watch(radioValue, newValue => {
  if (newValue && props.selectable) {
    if (props.isNoBankAccount) {
      emit('select', null)
    } else if (props.bankAccount) {
      emit('select', props.bankAccount.Id)
    }
  }
})

const inputId = computed(() => {
  if (props.isNoBankAccount) {
    return 'no-bank-account'
  }
  return `bank-account-${props.bankAccount?.Id}`
})

const dataTestId = computed(() => {
  if (props.isNoBankAccount) {
    return 'no-bank-account-option'
  }
  return `bank-account-item-${props.bankAccount?.Id}`
})

// Format bank account display name
// Format: "Bank Name •••• 1234" or "Bank Account •••• 1234" if name unavailable
const displayName = computed(() => {
  if (props.isNoBankAccount) {
    return props.noBankAccountLabel
  }

  const bankName = props.bankAccount?.BankName || 'Bank Account'
  const lastFour = props.bankAccount?.LastFourDigits || ''

  if (lastFour) {
    return `${bankName} •••• ${lastFour}`
  }

  // If no last four digits, just show bank name
  // Never show raw GID or full ID
  return bankName
})
</script>
